import type { KidDocumentForm } from '../../kidsV2/types/KidPdfGeneratorV2.types';

/**
 * Converts a Blob to a base64 string
 * @param blob - The blob to convert
 * @returns Promise that resolves to base64 string (without data URL prefix)
 */
export const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/**
 * Result of a PDF generation operation
 */
export interface PdfGenerationResult {
  blob: Blob;
  base64: string;
  objectUrl: string;
}

/**
 * Generates a PDF document from a KidDocumentForm payload
 * Handles the entire flow: mutation call -> blob creation -> base64 conversion -> object URL creation
 * 
 * @param payload - The KID document form data
 * @param mutateAsync - The mutation function from useGenerateKidDocument
 * @returns Promise that resolves to PdfGenerationResult containing blob, base64, and objectUrl
 */
export const generateKidPdf = async (
  payload: KidDocumentForm,
  mutateAsync: (data: KidDocumentForm) => Promise<any>,
): Promise<PdfGenerationResult> => {
  const response = await mutateAsync(payload);
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const base64 = await blobToBase64(blob);
  const objectUrl = URL.createObjectURL(blob);

  return {
    blob,
    base64,
    objectUrl,
  };
};

/**
 * Creates a minimal valid blank PDF and returns its object URL
 * Useful as a fallback when PDF generation fails
 * @returns Object URL for the blank PDF
 */
export const createMinimalBlankPdfUrl = (): string => {
  const minimalPdf = `%PDF-1.4
  1 0 obj
  << /Type /Catalog /Pages 2 0 R >>
  endobj
  2 0 obj
  << /Type /Pages /Kids [3 0 R] /Count 1 >>
  endobj
  3 0 obj
  << /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] >>
  endobj
  xref
  0 4
  0000000000 65535 f 
  0000000010 00000 n 
  0000000053 00000 n 
  0000000100 00000 n 
  trailer
  << /Root 1 0 R /Size 4 >>
  startxref
  150
  %%EOF`;

  const blob = new Blob([minimalPdf], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  return url;
};
